import { useCallback, useEffect, useMemo, useState } from "react";
import type { MontajListesi } from "@oasis/domain";
import { useVeri } from "@/lib/veri";
import { Band, Bos, Buton, Etiket, Hata, Kutu, Sayfa } from "@/components/parcalar";
import { SetSatiri, type SetOzet } from "./Planlama";
import { MontajDonusturucuKutusu, MontajKayitListesi, MontajSatirDugmesi } from "./MontajKuyruk";
import { MontajTopluYukleme } from "./MontajTopluYukleme";
import { cevir } from "@/lib/dil";
import { MONTAJ_UCU, MONTAJ_YENILEME_MS, montajListesiGetir, montajSiraIslemi } from "@/lib/montaj";
import { siraliYenile } from "@/lib/mrb-toplu";

export function Assembly() {
  /** `muayene`: muayene doğrulamada biten/görünür komponent sayısı — riser tamamı bitmeden de listeye düşer. */
  type Muayeneden = SetOzet & { muayene?: { biten: number; toplam: number } };
  /**
   * D-069: `muayeneAcik` = kuyruktaki riserin görünür komponentlerinde kalemi açılmamış REJECT'li ek doğrulama satırları ile o satırlardan
   * açılmış bekleyen kalemlerin sayısı. Sıfırdan büyükse muayene yeniden açıktır; riser kuyrukta kalır, satırının üstünde kırmızı çip durur.
   */
  type Kuyrukta = SetOzet & { muayeneAcik?: number };
  const { veri, hata, yenile } = useVeri<{ risers: Kuyrukta[]; muayeneden: Muayeneden[] }>("/assembly");
  /**
   * D-068: montaj kayıtları ve "Assembly report converter" kutusu ayrı uçtan gelir (`GET /api/montaj`). Bu uç gelmediyse ya da
   * hata verdiyse riser satırları ve "Assembly" düğmeleri yine çizilir (etiketler yalnız Finishing verisinden kurulur), iki yeni
   * kutu çizilmez: rapor kapısı yeni ucun arızasına bağlı kalmasın.
   */
  const { veri: montaj, setVeri: setMontaj, hata: montajHata } = useVeri<MontajListesi>(MONTAJ_UCU.liste);
  const [islem, setIslem] = useState<string | null>(null);
  /** D-068: "Upload Assembly reports" penceresi, kuyruk işlemi süren kayıt ve dönüştürücü rozetinin saati. */
  const [toplu, setToplu] = useState(false);
  const [siraMesgul, setSiraMesgul] = useState<string | null>(null);
  const [simdi, setSimdi] = useState(() => Date.now());

  /**
   * Sessiz yenileme (söküm listesindeki desen): hata ekranı açmaz, yalnız veriyi yerine koyar; dönüştürücü durumu canlı görünsün
   * diye 30 saniyede bir, sekme gizliyken atlanır. Aynı anda uçan yenilemelerden yalnız en son başlatılanın yanıtı yazılır.
   */
  const getir = useMemo(() => siraliYenile(montajListesiGetir, setMontaj), [setMontaj]);
  const tazele = useCallback(async () => {
    try {
      await getir();
    } catch {
      // Arka plan yenilemesi: bir sonraki turda yeniden denenir.
    }
    setSimdi(Date.now());
  }, [getir]);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void tazele();
    }, MONTAJ_YENILEME_MS);
    return () => window.clearInterval(t);
  }, [tazele]);

  /** Montaj kaydı değiştiren işlemden sonra iki liste de tazelenir (riser satırındaki etiketler kayıt listesinden okunur). */
  const ikisiniTazele = async () => {
    await Promise.all([tazele(), yenile()]);
  };

  const sira = async (id: string, i: "ekle" | "cikar") => {
    setIslem(null);
    setSiraMesgul(id);
    try {
      await montajSiraIslemi(id, i);
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setSiraMesgul(null);
      await ikisiniTazele();
    }
  };

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const kayitlar = new Map((montaj?.kayitlar ?? []).map((k) => [k.rn, k]));
  /** Riser satırının Assembly yuvası: düğme riserın Assembly sayfasını açar; rapor orada yüklenir ve kaldırılır. */
  const yuva = (oz: SetOzet) => <MontajSatirDugmesi rn={oz.rn} gonderildi={Boolean(oz.set?.gonderimTs)} asmVar={Boolean(oz.set?.asmRaporu)} kayit={kayitlar.get(oz.rn) ?? null} />;
  return (
    <Sayfa ust="5.5 · E-26" baslik="Finishing" alt={<>{cevir("Inspection finished and in Repair planning")} <b>{cevir("“Send to assembly”")}</b> {cevir("called risers are in a single list. Two reports are uploaded for each riser:")} <b>{cevir("🎨 Coating report")}</b> {cevir("(GOE coating form) +")} <b>{cevir("📄 Assembly report")}</b> {cevir("(FM-0012 print-sign-upload output). When both are uploaded")} <b>{cevir("maintenance process ends → MRB Document preparation (E-32)")}</b>. {cevir("Per line an NDT report (or No record available) is required before the Assembly report.")} {cevir("The Assembly report is uploaded on the riser's Assembly page: press Assembly on its row.")}</>}>
      <Hata mesaj={islem} />
      {!montaj && <Hata mesaj={montajHata} />}
      {montaj && <MontajDonusturucuKutusu d={montaj.donusturucu} simdi={simdi} />}
      {/* Tek liste (kullanıcı isteği 2026-09-02): gönderilmiş ve muayenesi bitmiş riser'lar ayrı
          başlıklar altında değil, tek başlık altında. Ayrım satır rozetiyle korunuyor —
          kuyruğa alınmamış riser'ın rapor yükleyemeyeceği görünsün. */}
      <Kutu baslik={cevir("Risers")} aksiyon={`${veri.risers.length + veri.muayeneden.length} riser`}>
        {veri.risers.length + veri.muayeneden.length ? (
          <>
            {veri.risers.map((oz) => (oz.muayeneAcik ?? 0) > 0 ? (
              <div key={oz.rn}>
                <div className="px-1 pt-2"><Etiket tone="bad">{cevir("inspection re-opened: additional REJECT waiting (see Triage)")}</Etiket></div>
                <SetSatiri oz={oz} mod="asm" onDegisti={yenile} setIslem={setIslem} asmYuvasi={yuva(oz)} />
              </div>
            ) : <SetSatiri key={oz.rn} oz={oz} mod="asm" onDegisti={yenile} setIslem={setIslem} asmYuvasi={yuva(oz)} />)}
            {veri.muayeneden.map((oz) => (
              <div key={oz.rn}>
                <div className="px-1 pt-2"><Etiket tone="warn">{cevir("not queued — \"Send to assembly\" from Repair planning")}</Etiket>{oz.muayene && <Etiket tone="info" className="ml-1">{cevir`inspection ${oz.muayene.biten}/${oz.muayene.toplam} components done`}</Etiket>}</div>
                <SetSatiri oz={oz} mod="asm" onDegisti={yenile} setIslem={setIslem} asmYuvasi={yuva(oz)} />
              </div>
            ))}
          </>
        ) : <Bos>{cevir("No risers yet; falls here with \"Send to assembly\" in Repair planning, or as soon as the first component is completed in inspection.")}</Bos>}
      </Kutu>
      {/* D-068: montaj kayıtları. Finishing listesinde olmayan arşiv riserlarını da taşır. "Skip" karşılığı yoktur. */}
      {montaj && (
        <MontajKayitListesi kayitlar={montaj.kayitlar} siraMesgul={siraMesgul} sira={(id, i) => void sira(id, i)}>
          <div className="flex flex-wrap gap-2 border-t border-line p-3"><Buton islem="evrak" onClick={() => setToplu(true)}>{cevir("Upload Assembly reports")}</Buton></div>
          <div className="px-3 pb-3"><Band tip="bilgi">{cevir("Upload one or many Assembly report PDFs at once with Upload Assembly reports, with or without their assembly inputs (R{rn}-assembly-input-EN.xlsx). The riser is read from the file name (e.g. Riser No 106 Assembly Traceability Record.pdf); if it cannot be read, you select the riser on that file's row. A PDF uploaded with its assembly input is read at once; a PDF uploaded alone joins the converter queue and the record waits for the excel. A report uploaded from that window opens an assembly record only: it does not complete the Finishing step and no NDT condition is checked. For a riser in the assembly queue, the Assembly report is uploaded on its Assembly page. A PDF that carries several risers is split into one PDF per riser before it is uploaded.")}</Band></div>
        </MontajKayitListesi>
      )}
      {/* Pencerenin satırları kendi bileşeninde durduğu için pencere açıkken yenilemek listeyi bozmaz. */}
      {toplu && <MontajTopluYukleme onKapat={() => { setToplu(false); void ikisiniTazele(); }} onYuklendi={() => void ikisiniTazele()} />}
      <Band tip="bilgi">{cevir("Report upload permission: Quality Eng. (Document); send permission: planning (Foreman / Supervisor / PM).")} {cevir("“No record available” (NDT): Quality Lead / PM.")} {cevir("Assembly page: uploading the report and the assembly input and the queue actions need the report upload permission.")} {cevir("Filling the Assembly form on screen and marking the print-out: Foreman, Field Supervisor.")}</Band>
    </Sayfa>
  );
}
